import {
    Address,
    Cart,
    CheckoutSelectors,
    Consignment,
    CustomerAddress,
    FormField,
    Country,
    ConsignmentAssignmentRequestBody,
    PhysicalItem,
} from '@bigcommerce/checkout-sdk'
import { withFormik, FormikProps } from 'formik'
import { lazy, object } from 'yup'
import React, { PureComponent, ReactNode } from 'react'
import { isEmpty, noop } from 'lodash'
import { createSelector } from 'reselect'
import { connect } from 'react-redux'

import { withCheckout, CheckoutContextProps } from '../../checkout'
import {
    isValidAddress,
    localizeAddress,
    AddressFormValues,
    getAddressFormFieldsValidationSchema,
} from '../../address'
import { withLanguage, TranslatedString, WithLanguageProps } from '../../locale'
import { Form, Fieldset, TextInput } from '../../ui/form'
import { LoadingOverlay } from '../../ui/loading'
import { Button, ButtonVariant } from '../../ui/button'
import { Alert, AlertType } from '../../ui/alert'
import { OrderComments } from '../../orderComments'

import { AssignItemFailedError } from '../../shipping/errors'
import hasSelectedShippingOptions from '../../shipping/hasSelectedShippingOptions'
import hasUnassignedLineItems from '../../shipping/hasUnassignedLineItems'
import StaticConsignmentItemList from '../../shipping/StaticConsignmentItemList'
import ShippingOptionsList from '../../shipping/shippingOption/ShippingOptionsList'
import { LocalizedGeography } from '../../geography'
import ShippingAddressForm from '../../shipping/ShippingAddressForm'
import findLineItems from '../../shipping/findLineItems'

import { tips } from '../locales'
import { addNewB3Address } from '../addresses/addNewB3Address'
import { validateAddress } from '../utils/util'
import extrasFiles from '../extrasFiles'

import './styles.scss'

export interface MultiShippingFormProps {
    addresses: CustomerAddress[]
    cart: Cart
    cartHasChanged: boolean
    consignments: Consignment[]
    customerMessage: string
    isLoading: boolean
    shouldShowOrderComments: boolean
    onSubmit(values: MultiShippingFormValues): void
    onUnhandledError(error: Error): void
    onUseNewAddress(address: Address, itemId: string): void
    countriesWithAutocomplete: string[]
    getFields(countryCode?: string): FormField[]
    googleMapsApiKey?: string
    shouldShowSaveAddress?: boolean
    shouldValidateSafeInput: boolean
}

export interface MultiShippingFormState {
    isEditing: boolean
    fields: FormField[]
    editConsignment: Consignment
    editingLineItems: (PhysicalItem & { rest?: number })[]
    isConsignmentEditorOpen: boolean
}

interface WithCheckoutStaticAddressProps {
    countries?: Country[]
    isLoadingOption(consignmentId?: string): boolean
    selectShippingOption(
        consignmentId: string,
        optionId: string
    ): Promise<CheckoutSelectors>
    invalidShippingMessage: string
    deleteConsignment(consignmentId: string): Promise<CheckoutSelectors>
    assignItemsToAddress(
        consignment: ConsignmentAssignmentRequestBody
    ): Promise<CheckoutSelectors>
}

interface B3Props {
    b3Company: any
    checkoutSettings: any
}

class MultiShippingForm extends PureComponent<
    B3Props &
    WithCheckoutStaticAddressProps &
    MultiShippingFormProps &
    WithLanguageProps &
    FormikProps<MultiShippingFormValues>,
    MultiShippingFormState
    > {
    static getDerivedStateFromProps({ consignments }: MultiShippingFormProps) {
        if (!consignments.length)
            return {
                isConsignmentEditorOpen: true,
            }

        return null
    }

    state: MultiShippingFormState = {
        isEditing: false,
        fields: [],
        isConsignmentEditorOpen: false,
        editConsignment: {} as Consignment,
        editingLineItems: [],
    }

    constructor(
        props: B3Props &
            WithCheckoutStaticAddressProps &
            MultiShippingFormProps &
            WithLanguageProps &
            FormikProps<MultiShippingFormValues>
    ) {
        super(props)
    }

    componentDidMount() {
        const { consignments, cart } = this.props

        this.setState({
            isConsignmentEditorOpen: !consignments.length,
            editConsignment: this.basicConsignment,
            editingLineItems: findLineItems(cart, this.basicConsignment),
        })
    }

    get basicConsignment() {
        const { b3Company } = this.props

        return {
            shippingAddress: { ...b3Company.basicAddressInfo },
            lineItemIds: this.getRestLineItemIds(),
        } as Consignment
    }

    get shouldShowConsignmentEditor() {
        const { cart, consignments } = this.props
        const { isConsignmentEditorOpen } = this.state

        return (
            isConsignmentEditorOpen ||
            hasUnassignedLineItems(consignments, cart.lineItems)
        )
    }

    render(): ReactNode {
        const {
            shouldShowOrderComments,
            isLoading,
            consignments,
            cartHasChanged,
            cart,
        } = this.props
        const { isConsignmentEditorOpen } = this.state

        return (
            <Form>
                <Fieldset
                    id='checkout-shipping-options'
                    legend={
                        <>
                            {cartHasChanged && (
                                <Alert type={AlertType.Error}>
                                    <strong>
                                        <TranslatedString id='shipping.cart_change_error' />
                                    </strong>
                                </Alert>
                            )}
                        </>
                    }
                >
                    {(!this.shouldShowConsignmentEditor ||
                        hasUnassignedLineItems(consignments, cart.lineItems)) &&
                        this.renderConsignments()}
                </Fieldset>

                {this.shouldShowConsignmentEditor && (
                    <>
                        {this.renderLineItemsEditor()}
                        {this.renderConsignmentEditor()}
                    </>
                )}

                {!this.shouldShowConsignmentEditor && shouldShowOrderComments && (
                    <OrderComments />
                )}

                <div className='form-actions'>
                    {this.shouldShowConsignmentEditor ? (
                        <>
                            <Button
                                disabled={this.shouldDisableEditorSubmit()}
                                isLoading={isLoading}
                                variant={ButtonVariant.Primary}
                                onClick={this.handleEditorSubmit}
                                type='button'
                            >
                                <TranslatedString id='common.continue_action' />
                            </Button>
                            {isConsignmentEditorOpen && (
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    onClick={this.handleEditorCancel}
                                    type='button'
                                >
                                    <TranslatedString id='common.cancel_action' />
                                </Button>
                            )}
                        </>
                    ) : (
                            <Button
                                disabled={this.shouldDisableSubmit()}
                                id='checkout-shipping-continue'
                                isLoading={isLoading}
                                variant={ButtonVariant.Primary}
                                onClick={this.submit}
                            >
                                <TranslatedString id='common.continue_action' />
                            </Button>
                    )}
                </div>
            </Form>
        )
    }

    private submit = () => {
        const { onSubmit, values } = this.props

        onSubmit(values)
    }

    private handleEditorSubmit = async () => {
        const { editConsignment, editingLineItems } = this.state
        const { onUnhandledError, assignItemsToAddress, values, addresses, getFields, checkoutSettings } = this.props

        const { shippingAddress } = editConsignment
        if (!shippingAddress) return

        const formFields = getFields(shippingAddress && shippingAddress.countryCode)
        const isValid = validateAddress(shippingAddress, addresses, formFields)

        const {
            shippingAddress: {
                shouldSaveAddress,
                company
            },
            b2bCustomFields,
        } = values

        const lineItems = editingLineItems
            .map(({ id: itemId, quantity }) => ({
                itemId,
                quantity,
            }))
            .filter(({ quantity }) => !!quantity)

        try {
            const addToB3 = checkoutSettings['shipping.address.save'] ? addNewB3Address({
                newAddress: {
                    ...shippingAddress,
                    addressId: isValid ? undefined : '',
                    shouldSaveAddress,
                    company,
                },
                billingSameAsShipping: false,
                isBillingMode: false,
                b2bCustomFields: b2bCustomFields,
            }) : noop()

            const assignToAddress = assignItemsToAddress({
                shippingAddress: {
                    ...shippingAddress,
                    company,
                },
                lineItems,
            })

            await Promise.all([addToB3, assignToAddress])
        } catch (e) {
            onUnhandledError(new AssignItemFailedError(e))
        }
        this.handleEditorCancel()
    }

    private handleEditorCancel = () => {
        const { cart } = this.props

        this.setState({
            isConsignmentEditorOpen: false,
            editConsignment: this.basicConsignment,
            editingLineItems: findLineItems(cart, this.basicConsignment),
        })
    }

    private handleEditorOpen = (consignment: Consignment) => () => {
        const { cart, setFieldValue } = this.props

        const lineItems = findLineItems(cart, consignment)

        this.setState(
            {
                isConsignmentEditorOpen: true,
                editConsignment: consignment,
                editingLineItems: lineItems,
            },
            () => setFieldValue('shippingAddress', consignment.shippingAddress)
        )
    }

    private shouldDisableEditorSubmit: () => boolean = () => {
        const { language, getFields, values: { b2bCustomFields } } = this.props
        const { editConsignment, editingLineItems } = this.state

        const { shippingAddress }: any = editConsignment
        const isShippingAddressValid =
            !!shippingAddress &&
            getAddressFormFieldsValidationSchema({
                language,
                formFields: getFields(shippingAddress.countryCode),
            }).isValidSync(shippingAddress)
       
        
        const  getB3ShippingFlieds = extrasFiles.getB3ShippingAddressExtras()
        let isExtrasValid = false
        if (getB3ShippingFlieds.length) {
            getB3ShippingFlieds.map((item: any) => {
                if (item.required && !b2bCustomFields[item.name]) {
                    isExtrasValid = true
                }
            })
        }

        const lineTotals = editingLineItems.reduce((result, lineItem) => result += lineItem.quantity, 0)

        return !isShippingAddressValid || !lineTotals || isExtrasValid
    }

    private shouldDisableSubmit: () => boolean = () => {
        const { isLoading, consignments, getFields, countries } = this.props

        const isAllValid = consignments.every(consignment => {
            const { shippingAddress } = consignment

            const fields = getFields(shippingAddress.countryCode)
            const address = localizeAddress(shippingAddress, countries)
            return !fields
                ? !isEmpty(address)
                : isValidAddress(
                    address,
                    fields.filter(field => !field.custom)
                )
        })

        return isLoading || !hasSelectedShippingOptions(consignments) || !isAllValid
    }

    renderConsignments(): ReactNode {
        const { consignments, getFields, countries, cart } = this.props
        if (!consignments.length) return null

        return (
            <div className='multi-consignments-container optimizedCheckout-overlay'>
                {consignments.map(consignment => {
                    const {
                        shippingAddress,
                        id,
                        selectedShippingOption,
                        availableShippingOptions,
                    } = consignment

                    const fields = getFields(shippingAddress.countryCode)
                    const address = localizeAddress(shippingAddress, countries)
                    const isValid = !fields
                        ? !isEmpty(address)
                        : isValidAddress(
                            address,
                            fields.filter(field => !field.custom)
                        )

                    return (
                        <div key={id} className='multi-address-listItem'>
                            {this.renderStaticAddress(address, id, consignment)}

                            <StaticConsignmentItemList
                                cart={cart}
                                consignment={consignment}
                            />

                            {this.renderShippingOptionsList(
                                consignment,
                                isValid,
                                id,
                                selectedShippingOption,
                                availableShippingOptions
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    renderStaticAddress(
        address: Address & LocalizedGeography,
        id: string,
        consignment: Consignment
    ): ReactNode {
        return (
            <div className='checkout-address--static'>
                <div className='multi-address--info'>
                    {(address.firstName || address.lastName) && (
                        <p className='fn address-entry'>
                            <span className='first-name'>{`${address.firstName} `}</span>
                            <span className='family-name'>{address.lastName}</span>
                        </p>
                    )}

                    {(address.phone || address.company) && (
                        <p className='address-entry'>
                            <span className='company-name'>{`${address.company} `}</span>
                            <span className='tel'>{address.phone}</span>
                        </p>
                    )}

                    <div className='adr'>
                        <p className='street-address address-entry'>
                            <span className='address-line-1'>{`${address.address1} `}</span>
                            {address.address2 && (
                                <span className='address-line-2'>
                                    {` / ${address.address2}`}
                                </span>
                            )}
                        </p>

                        <p className='address-entry'>
                            {address.city && (
                                <span className='locality'>{`${address.city}, `}</span>
                            )}
                            {address.localizedProvince && (
                                <span className='region'>{`${address.localizedProvince}, `}</span>
                            )}
                            {address.postalCode && (
                                <span className='postal-code'>{`${address.postalCode} / `}</span>
                            )}
                            {address.localizedCountry && (
                                <span className='country-name'>{`${address.localizedCountry} `}</span>
                            )}
                        </p>
                    </div>
                </div>
                <div className='multi-address--actions'>
                    <a onClick={this.handleEditorOpen(consignment)}>
                        {tips['b3.multiShipping.address.edit']}
                    </a>
                    <a onClick={this.handleDeleteConsignment(id)}>
                        {tips['b3.multiShipping.address.remove']}
                    </a>
                </div>
            </div>
        )
    }

    private renderNoShippingOptions(message: ReactNode): ReactNode {
        return (
            <div className='shippingOptions-panel optimizedCheckout-overlay'>
                <p className='shippingOptions-panel-message optimizedCheckout-primaryContent'>
                    {message}
                </p>
            </div>
        )
    }

    private renderShippingOptionsList(
        consignment: Consignment,
        isValid: boolean,
        id: string,
        selectedShippingOption: any,
        availableShippingOptions: any
    ): ReactNode {
        const {
            cart,
            isLoadingOption,
            selectShippingOption,
            invalidShippingMessage,
        } = this.props

        if (!isValid) {
            return (
                <LoadingOverlay isLoading={isLoadingOption()}>
                    {this.renderNoShippingOptions(
                        <TranslatedString id={'shipping.select_shipping_address_text'} />
                    )}
                </LoadingOverlay>
            )
        }

        return (
            <>
                <ShippingOptionsList
                    cart={ cart }
                    consignment={ consignment }
                    consignmentId={id}
                    inputName={`shippingOptionIds.${id}`}
                    isLoading={isLoadingOption(id)}
                    onSelectedOption={selectShippingOption}
                    selectedShippingOptionId={
                        selectedShippingOption && selectedShippingOption.id
                    }
                    shippingOptions={availableShippingOptions}
                />

                {(!availableShippingOptions || !availableShippingOptions.length) && (
                    <LoadingOverlay
                        hideContentWhenLoading
                        isLoading={isLoadingOption(id)}
                    >
                        {this.renderNoShippingOptions(invalidShippingMessage)}
                    </LoadingOverlay>
                )}
            </>
        )
    }

    handleDeleteConsignment = (id: string) => async () => {
        const { deleteConsignment } = this.props

        await deleteConsignment(id)
        this.handleEditorCancel()
    }

    renderConsignmentEditor(): ReactNode {
        const {
            addresses,
            countries,
            consignments,
            countriesWithAutocomplete,
            getFields,
            googleMapsApiKey,
            isLoading,
            shouldShowSaveAddress,
        } = this.props

        const {
            editConsignment: { shippingAddress },
        } = this.state

        return (
            <Fieldset>
                <ShippingAddressForm
                    address={shippingAddress}
                    addresses={addresses}
                    consignments={consignments}
                    countries={countries}
                    countriesWithAutocomplete={countriesWithAutocomplete}
                    formFields={getFields(shippingAddress && shippingAddress.countryCode)}
                    googleMapsApiKey={googleMapsApiKey}
                    isLoading={isLoading}
                    onAddressSelect={this.handleSelectAddress}
                    onFieldChange={this.handleFieldChange}
                    onUseNewAddress={this.handleUseNewAddress}
                    shouldShowSaveAddress={shouldShowSaveAddress}
                />
            </Fieldset>
        )
    }

    private handleSelectAddress = (address: any) => {
        const { editConsignment } = this.state
        const { getFields } = this.props

        const shippingAddress = getFields(address.countryCode).reduce((result, { name, custom, }) => {
            if (custom) {
                const filed = address.customFields.find(({ fieldId }: { fieldId: string }) => fieldId === name);
                filed && result.customFields.push(filed)
            } else result[name] = address[name]

            return result
        }, {
            customFields: [],
            stateOrProvince: address.stateOrProvince,
        } as any)

        this.setState({
            editConsignment: {
                ...editConsignment,
                shippingAddress,
            } as Consignment,
        })
    }

    private handleFieldChange = async (name: string, value: any) => {
        const { editConsignment } = this.state
        const { setFieldValue } = this.props

        const { shippingAddress } = editConsignment

        this.setState({
            editConsignment: {
                ...editConsignment,
                shippingAddress: {
                    ...shippingAddress,
                    [name]: value,
                },
            } as Consignment,
        })

        if (name === 'countryCode') {
            setFieldValue('shippingAddress.stateOrProvince', '')
            setFieldValue('shippingAddress.stateOrProvinceCode', '')
        }

        // // Enqueue the following code to run after Formik has run validation
        await new Promise(resolve => setTimeout(resolve))

        // const { isValid } = this.props
    }

    private handleUseNewAddress = () => {
        const { setFieldValue } = this.props

        this.setState(
            {
                editConsignment: {
                    ...this.basicConsignment,
                } as Consignment,
            },
            () =>
                setFieldValue('shippingAddress', this.basicConsignment.shippingAddress)
        )
    }

    private renderLineItemsEditor(): ReactNode {
        const { editingLineItems: lineItems } = this.state

        return (
            <div className='multi-line-items-editor-container optimizedCheckout-overlay'>
                {lineItems.map(lineItem => (
                    <div className='multi-line-item' key={lineItem.id}>
                        <figure className='product-figure'>
                            <img src={lineItem.imageUrl} />
                        </figure>
                        <span>{lineItem.name} </span>
                        <span dangerouslySetInnerHTML={{ __html: ' &times;' }}></span>
                        <div className='multi-line-item-input'>
                            <TextInput
                                value={lineItem.quantity}
                                onChange={this.setLineItemQuantity(lineItem.id)}
                            />
                        </div>
                        <span>
                            {lineItem?.rest || 0} {tips['b3.multiShipping.shipped.toAddress']}
                        </span>
                    </div>
                ))}
            </div>
        )
    }

    private getRestLineItemIds() {
        const { consignments, cart } = this.props

        if (!hasUnassignedLineItems(consignments, cart.lineItems)) return []
        return cart.lineItems.physicalItems
            .filter(
                ({ id, addedByPromotion }) =>
                    !addedByPromotion &&
                    !consignments.some(consignment =>
                        consignment.lineItemIds.includes(id as string)
                    )
            )
            .map(({ id }) => id)
    }

    private setLineItemQuantity = (id: string | number) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { editingLineItems } = this.state
        const { cart } = this.props

        const physicalItem = cart.lineItems.physicalItems.find(
            item => item.id === id
        )
        if (!physicalItem) return

        const { quantity } = physicalItem

        const newEditingLineItems = editingLineItems.map(lineItem => {
            let { quantity: newQuantity, rest = 0 } = lineItem

            if (lineItem.id === id) {
                const value = +e.target.value
                newQuantity = Math.max(
                    Math.min(quantity, isNaN(value) ? newQuantity : value),
                    0
                )
                rest = quantity - newQuantity
            }

            return {
                ...lineItem,
                quantity: newQuantity,
                rest,
            }
        })

        this.setState({
            editingLineItems: newEditingLineItems,
        })
    }
}

export interface MultiShippingFormValues {
    [x: string]: any
    orderComment: string
}

const isLoadingSelector = createSelector(
    (_: CheckoutSelectors, { isLoading }: MultiShippingFormProps) => isLoading,
    ({ statuses }: CheckoutSelectors) => statuses.isLoadingShippingOptions,
    ({ statuses }: CheckoutSelectors) => statuses.isSelectingShippingOption,
    ({ statuses }: CheckoutSelectors) => statuses.isUpdatingConsignment,
    ({ statuses }: CheckoutSelectors) => statuses.isCreatingConsignments,
    (
        isLoading,
        isLoadingShippingOptions,
        isSelectingShippingOption,
        isUpdatingConsignment,
        isCreatingConsignments
    ) => {
        return (consignmentId?: string) => {
            return (
                isLoading ||
                isLoadingShippingOptions() ||
                isSelectingShippingOption(consignmentId) ||
                isUpdatingConsignment(consignmentId) ||
                isCreatingConsignments()
            )
        }
    }
)

export function mapToStaticAddressProps(
    { checkoutService, checkoutState }: CheckoutContextProps,
    props: MultiShippingFormProps
): WithCheckoutStaticAddressProps | null {
    const {
        data: { getShippingCountries, getConfig },
    } = checkoutState

    const config = getConfig()

    if (!config) return null
    const { shippingQuoteFailedMessage } = config.checkoutSettings

    return {
        countries: getShippingCountries(),
        isLoadingOption: isLoadingSelector(checkoutState, props),
        selectShippingOption: checkoutService.selectConsignmentShippingOption,
        invalidShippingMessage: shippingQuoteFailedMessage,
        deleteConsignment: checkoutService.deleteConsignment,
        assignItemsToAddress: checkoutService.assignItemsToAddress,
    }
}

const mapStateToProps = (state: { global: { checkoutSettings: any }, company: any }) => {
    const {
        global: {
            checkoutSettings,
        },
        company: b3Company,

    } = state

    return {
        b3Company,
        checkoutSettings,
    }
}

export default connect(mapStateToProps)(
    withLanguage(
        withFormik<
            MultiShippingFormProps & WithLanguageProps & B3Props,
            MultiShippingFormValues
        >({
            handleSubmit: (values, { props: { onSubmit } }) => {
                onSubmit(values)
            },
            mapPropsToValues: ({ customerMessage, consignments, b3Company }) => {
                const shippingOptionIds: { [id: string]: string } = {}

                    ; (consignments || []).forEach(consignment => {
                        shippingOptionIds[consignment.id] = consignment.selectedShippingOption
                            ? consignment.selectedShippingOption.id
                            : ''
                    })

                return {
                    orderComment: customerMessage,
                    shippingOptionIds,
                    shippingAddress: b3Company.basicAddressInfo,
                    b2bCustomFields: extrasFiles.setGoBackEcho('shipping')
                }
            },
            validationSchema: ({
                language,
                getFields,
                shouldValidateSafeInput,
            }: MultiShippingFormProps & WithLanguageProps) =>
                object({
                    shippingAddress: lazy<Partial<AddressFormValues>>(formValues =>
                        getAddressFormFieldsValidationSchema({
                            language,
                            shouldValidateSafeInput,
                            formFields: getFields(formValues && formValues.countryCode),
                        })
                    ),
                    b2bCustomFields: lazy<Partial<any>>( () =>
                        extrasFiles.getB3FormFieldsValidationSchema({
                                translate: extrasFiles.getTranslateAddressError(language),
                                formFields: extrasFiles.getB3ShippingAddressExtras(),
                            })
                        ),
                }),
            enableReinitialize: true,
        })(withCheckout(mapToStaticAddressProps)(MultiShippingForm))
    )
)
